// ── @matix/auth ───────────────────────────────────────────────────────────
//
// React auth context shared by the three POS apps.
// Mirrors warsha's pattern (token in localStorage, 401 → /login) but
// formalizes it as a Provider + hook so we can compose with TanStack Query
// and react-router cleanly.
//
// Usage:
//   <AuthProvider>
//     <App />
//   </AuthProvider>
//
//   const { cashier, login, logout, isAuthenticated } = useAuth();

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authAPI, getClient, getApiErrorMessage } from '@matix/api-client';
import type { CashierJwtPayload, LoginRequest } from '@matix/types';

// ── JWT decoding (no verification — purely for reading payload) ─────────
function decodeJwt(token: string): CashierJwtPayload | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1];
    if (!payload) return null;
    // base64url → base64
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    // Browser uses atob; the Buffer fallback (Node/SSR) is reached via a
    // typed globalThis cast so this browser package needs no @types/node.
    const nodeBuffer = (globalThis as unknown as {
      Buffer?: { from(s: string, enc: string): { toString(enc: string): string } };
    }).Buffer;
    let json: string;
    if (typeof atob === 'function') {
      // atob yields a Latin1 byte-string (one char per byte). Re-decode it as
      // UTF-8 so multi-byte characters — e.g. an Arabic branch_name in the
      // payload — aren't mangled into mojibake ("فرع" → "Ù�Ø±Ø¹").
      const binary = atob(padded);
      const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
      json = new TextDecoder('utf-8').decode(bytes);
    } else {
      json = nodeBuffer!.from(padded, 'base64').toString('utf8');
    }
    return JSON.parse(json) as CashierJwtPayload;
  } catch {
    return null;
  }
}

function isExpired(payload: CashierJwtPayload | null): boolean {
  if (!payload) return true;
  if (typeof payload.exp !== 'number') return false;
  return Date.now() / 1000 >= payload.exp - 5; // 5s skew buffer
}

// ── Context shape ────────────────────────────────────────────────────────
export interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  cashier: CashierJwtPayload | null;
  login: (payload: LoginRequest) => Promise<void>;
  logout: () => Promise<void>;
  error: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ── Provider ─────────────────────────────────────────────────────────────
export interface AuthProviderProps {
  children: ReactNode;
  /** Called when the user logs out or session expires (e.g. navigate to /login). */
  onLogout?: () => void;
}

export function AuthProvider({ children, onLogout }: AuthProviderProps) {
  const client = getClient();
  const [cashier, setCashier] = useState<CashierJwtPayload | null>(() => {
    const tok = client.getToken();
    if (!tok) return null;
    const p = decodeJwt(tok);
    if (isExpired(p)) {
      client.clearToken();
      return null;
    }
    return p;
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Token expiry watcher — auto-logout when JWT expires
  useEffect(() => {
    if (!cashier?.exp) return;
    const msUntilExpiry = cashier.exp * 1000 - Date.now();
    if (msUntilExpiry <= 0) {
      client.clearToken();
      setCashier(null);
      onLogout?.();
      return;
    }
    const handle = setTimeout(() => {
      client.clearToken();
      setCashier(null);
      onLogout?.();
    }, msUntilExpiry);
    return () => clearTimeout(handle);
  }, [cashier?.exp, client, onLogout]);

  const login = useCallback(
    async (payload: LoginRequest) => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await authAPI.login(payload);
        client.setToken(res.token);
        const decoded = decodeJwt(res.token);
        if (!decoded) throw new Error('Invalid token returned by server');
        setCashier(decoded);
      } catch (err) {
        const msg = getApiErrorMessage(err, 'Login failed');
        setError(msg);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [client],
  );

  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      await authAPI.logout();
    } catch {
      // ignore — token is cleared either way
    } finally {
      client.clearToken();
      setCashier(null);
      setIsLoading(false);
      onLogout?.();
    }
  }, [client, onLogout]);

  const value = useMemo<AuthContextValue>(
    () => ({
      isAuthenticated: cashier !== null,
      isLoading,
      cashier,
      login,
      logout,
      error,
    }),
    [cashier, isLoading, login, logout, error],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ── Hook ─────────────────────────────────────────────────────────────────
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>');
  }
  return ctx;
}

// ── Route guard ──────────────────────────────────────────────────────────
export interface RequireAuthProps {
  children: ReactNode;
  fallback?: ReactNode;
}

export function RequireAuth({ children, fallback = null }: RequireAuthProps) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? <>{children}</> : <>{fallback}</>;
}
